import {
  ConnectableElement,
  Connector,
  ConnectorInstance,
  ConnectorParameters,
  Datapoint,
  DefaultDatapointTypes,
  EventDatapointParameters,
  JSAPIDatapointParameters,
  JSONObject,
  TYPE,
} from '@olive/oli-types';
import * as EventEmitter from 'events';
import * as JsonataModule from 'jsonata';
import APIManager from '../APIManager';
import ConnectivityHolder,{ConnectivityDatapoint} from '../ConnectivityHolder';
import ConnectivityManager from '../ConnectivityManager';
import {getDatapointFromConnectorElement} from '../utils';
import AbstractDispatcher from './AbstractDispatcher';
// import BroadcastDispatcher from './BroadcastDispatcher';
import {isConnectorDefinition,isConnectorInstance,isConnectorTask,isDatapointDefinition,isDatapointInstance} from '@olive/oli-types/src/typeutil/isOfType';
import WindowDispatcher from './WindowDispatcher';

const jsonata = JsonataModule.default;

const SUPPORTED_TYPES = [
  'olive',
  'event',
  'jsAPI',
  'windowmessage',
  'broadcast',
];

export const CLIENT_DISPATCHER_ID = 'client-dispatcher';

function extractJsonataExpression(str: string): string | null {
  const startIndex = str.indexOf('${');
  if (startIndex === -1) {
    return null; // No expression
  }

  let braceLevel = 0;
  let i = startIndex;

  // Scan until we find the matching '}'
  while (i < str.length) {
    if (str[i] === '{') {
      braceLevel++;
    } else if (str[i] === '}') {
      braceLevel--;
      // If we've closed all '{', we're at the matching '}'
      if (braceLevel === 0) {
        // Return everything *between* "${" and this "}"
        return str.substring(startIndex + 2, i);
      }
    }
    i++;
  }
  return null;
}

export default class ClientDispatcher extends AbstractDispatcher {
  eventManager: EventEmitter;
  apiManager: APIManager;
  connectivityManager: ConnectivityManager;
  // broadcastDispatcher: BroadcastDispatcher;
  windowDispatcher: WindowDispatcher;

  constructor(
    connectivityHolder: ConnectivityHolder,
    eventManager: EventEmitter,
    apiManager: APIManager,
    connectivityManager: ConnectivityManager
  ) {
    super(connectivityHolder);
    this.eventManager = eventManager;
    this.apiManager = apiManager;
    this.connectivityManager = connectivityManager;
    // this.broadcastDispatcher = new BroadcastDispatcher(
    //   connectivityHolder,
    //   connectivityManager
    // );
    this.windowDispatcher = new WindowDispatcher(
      connectivityHolder,
      connectivityManager
    );
  }

  async dispatch(props: { id: string; input?: object }): Promise<unknown> {
    const datapoint = this.connectivityHolder.get(props.id) as ConnectableElement;
    console.log(datapoint, datapoint.type);
    switch (datapoint.type) {
      case TYPE.CONNECTOR:
      case 'connectorInstance':
        const result = await this.dispatchConnector(
          datapoint as Connector,
          props.input
        );
        return result;
      case TYPE.DATAPOINT:
        return await this.dispatchDatapoint(
          datapoint as ConnectivityDatapoint,
          props.input,
          props
        );
      default:
        console.warn(`Datapoint Type ${datapoint.type} not supported!`);
        return `Datapoint Type ${datapoint.type} not supported`;
    }
  }

  canDispatch(props: { id: string }): boolean {
    try {
      const { datapoint, type } = this.connectivityHolder.get(props.id) as Datapoint;
      if ([TYPE.CONNECTOR, 'connectorInstance'].includes(type)) {
        return true;
      }
      const datapointType = datapoint.type;
      return SUPPORTED_TYPES.includes(datapointType);
    } catch {
      return false;
    }
  }

  canDispatchType(type: string): boolean {
    return SUPPORTED_TYPES.includes(type);
  }

  async dispatchConnector(
    connector: Connector | ConnectorInstance,
    input?: object
  ): Promise<unknown> {
    let connectorConfig: ConnectorParameters;
    if (isConnectorDefinition(connector)) {
      connectorConfig = connector.connector;
    }
    else if (isConnectorInstance(connector)) {
      connectorConfig = connector;
    }
    else {
      console.error('Unknown connector type: ', connector);
      return null;
    }

    console.log('connectorConfig: ', connectorConfig);
    const resultMap = {};
    let previousResult = input;
    let lastResult = null;
    for (let i = 0; i < connectorConfig.elements.length; i++) {
      const datapoint = connectorConfig.elements[i];
      let ref: string = null;
      let parameters: JSONObject = null;
      if (isConnectorTask(datapoint) || isDatapointInstance(datapoint)) {
        if ('ref' in datapoint) {
          ref = datapoint.ref;
        }
        if (input && ref && input[ref]) {
          parameters = { ...datapoint.parameters, ...input[ref] };
        }
        else {
          parameters = datapoint.parameters;
        }
      }
      const resolvedConnectivity = getDatapointFromConnectorElement(datapoint, this.connectivityHolder);

      // if no data point that returns a result just take the input as result
      if (!resolvedConnectivity || (isDatapointDefinition(resolvedConnectivity) && resolvedConnectivity.datapoint.type === DefaultDatapointTypes.EVENT)) {
        resultMap[ref] = input;
        continue;
      }

      let resolvedInstanceParameters = parameters;
      if (parameters) {
        resolvedInstanceParameters = await this.resolveInstanceParameters(parameters, resultMap);
      }
      const enrichedInput = {
        ...previousResult,
        ...resolvedInstanceParameters,
      };


      console.log('invoking endoint with----------', previousResult, parameters, enrichedInput);

      let result = await this.connectivityManager.invoke({
        id: resolvedConnectivity.id,
        parameters: resolvedInstanceParameters,
        input: enrichedInput,
      });

      lastResult = result;
      if (Array.isArray(result)) {
        result = { array: result };
      }

      resultMap[ref] = result;
      previousResult = resultMap[ref];
    }
    return lastResult;
  }



  private async resolveInstanceParameters(
    params: any,
    resultMap: { [key: string]: object }
  ): Promise<any> {
    const newParams = Array.isArray(params) ? [...params] : { ...params };

    for (const key in newParams) {
      const value = newParams[key];

      // 1) If it's a string that *might* contain ${...}:
      if (typeof value === 'string') {
        // Attempt to extract a JSONata expression
        const extracted = extractJsonataExpression(value);
        if (extracted !== null) {
          try {
            // Evaluate the JSONata expression against resultMap
            const result = await jsonata(extracted).evaluate(resultMap);
            // Store the evaluated result back
            newParams[key] = result;
          } catch (error) {
            console.error(`Error evaluating JSONata expression: ${value}`, error);
          }
        }
      }
      // 2) If it's an object or an array, recurse
      else if (typeof value === 'object' && value !== null) {
        newParams[key] = await this.resolveInstanceParameters(value, resultMap);
      }
    }

    return newParams;
  }

  // private async invokeDatapoint(
  //   datapointConfig: any,
  //   input?: unknown,
  //   isSource: boolean = true
  // ): Promise<unknown> {
  //   const datapoint = {
  //     id: isString(datapointConfig)
  //       ? datapointConfig
  //       : isSource
  //         ? datapointConfig.source.id
  //         : datapointConfig.destination.instanceID || datapointConfig.destination.id,
  //     type: isString(datapointConfig)
  //       ? TYPE.CONNECTOR
  //       : isSource
  //         ? datapointConfig.source.type
  //         : datapointConfig.destination.type,
  //   };

  //   if (datapoint.type === TYPE.CONNECTOR || !this.canDispatchType(datapoint.type)) {
  //     const serverResult = await this.connectivityManager.invoke({
  //       id: datapoint.id,
  //       input,
  //     });
  //     return serverResult;
  //   }

  //   const connectivityDatapoint = this.connectivityHolder.get(datapoint.id) as Datapoint;
  //   const result = await this.connectivityManager.invoke({
  //     id: connectivityDatapoint.id,
  //     input,
  //   });

  //   return result;
  // }

  async dispatchDatapoint(
    datapoint: ConnectivityDatapoint,
    input?: object,
    props?: { id: string; input?: object }
  ): Promise<unknown> {
    console.log(datapoint);

    if (datapoint.datapoint.type === DefaultDatapointTypes.EVENT) {
      const eventDatapoint = datapoint.datapoint as EventDatapointParameters;
      return this.dispatchEvent(eventDatapoint.event.topic, input);
    }
    if (datapoint.datapoint.type === DefaultDatapointTypes.JS_API) {
      //@ts-ignore TODO according to types this is wrong, check if actually needed and correct
      const parameters = props?.parameters;
      let componentInstanceID = '';
      if (parameters) {
        componentInstanceID = parameters.componentInstanceID;
      } else if (datapoint.parameters) {
        componentInstanceID = datapoint.parameters.componentInstanceID as string;
      }
      const { ...datapointRest } = datapoint.parameters;
      const { ...propsRest } = parameters || {};
      const jsAPIDatapoint = datapoint.datapoint as JSAPIDatapointParameters;
      return this.dispatchJSAPI({
        apiName: jsAPIDatapoint.jsAPI.api,
        componentInstanceID,
        input,
        props: { ...datapointRest, ...propsRest },
      });
    }
    // if (this.broadcastDispatcher.canDispatch({ id: datapoint.id, datapoint })) {
    //   return await this.broadcastDispatcher.dispatch({
    //     id: datapoint.id,
    //     input,
    //   });
    // }
    if (this.windowDispatcher.canDispatch({ id: datapoint.id, datapoint })) {
      return await this.windowDispatcher.dispatch({
        id: datapoint.id,
        input,
      });
    }
    console.warn(
      'Currently unsupported datapointtype for client side: ' + datapoint.datapoint.type
    );
    return null;
  }


  dispatchEvent(topic: string, params: object) {
    this.eventManager.emit(topic, params);
  }

  dispatchJSAPI({
    apiName,
    componentInstanceID,
    input,
    props,
  }: {
    apiName: string;
    componentInstanceID: string;
    input: object;
    props: object;
  }) {
    const apiID = componentInstanceID + '-' + apiName;
    const api = this.apiManager.get(apiID);
    if (api) {
      return api({
        ...props,
        ...input
      });
    } else {
      console.error('Could not find api with id ' + apiID);
    }
  }
}
